import {
	cachedIntlFormatter,
	ChilizLogoRounded,
	RewardPoints,
	SsuLogoPlaceholder,
	type TransactionSummaryDTO,
	useLocale
} from '@capsicum/common';
import { ShortCode } from '@capsicum/socios-core';

const formatCurrency = ({
	amount,
	currencyShortCode,
	isNegativeBalance,
	locale
}: TransactionSummaryDTO & { locale: string }): string => {
	if (currencyShortCode === undefined || amount === undefined || isNegativeBalance === undefined) {
		return 'N/A';
	}

	const numberAmount = Number(amount);

	if (isNaN(numberAmount)) {
		return 'N/A';
	}

	// Format the number amount according to the locale
	const formatter = cachedIntlFormatter(locale, {
		maximumFractionDigits: 2,
		minimumFractionDigits: 2
	});

	const formattedAmount = formatter.format(numberAmount);

	// Append the cryptocurrency code at the end
	return `${isNegativeBalance ? '-' : '+'}${formattedAmount} ${currencyShortCode}`;
};

export type UseTransactionCardParameter = TransactionSummaryDTO & { partnerLogoUrl: string | undefined };

const logoOrPlaceholder = ({ currencyShortCode, partnerLogoUrl }: UseTransactionCardParameter) => {
	if (partnerLogoUrl) {
		return partnerLogoUrl;
	}

	if (currencyShortCode === ShortCode.CHZ) {
		return ChilizLogoRounded;
	}

	if (currencyShortCode === ShortCode.RP) {
		return RewardPoints;
	}

	return SsuLogoPlaceholder;
};

export const useTransactionCard = (params: UseTransactionCardParameter) => {
	const { amount, currencyShortCode, isNegativeBalance } = params;

	const { locale } = useLocale();

	const formattedCurrency = formatCurrency({ amount, currencyShortCode, isNegativeBalance, locale });

	const partnerLogoOrPlaceholderUrl = logoOrPlaceholder(params);

	return {
		formattedCurrency,
		isCurrencyRP: currencyShortCode === ShortCode.RP,
		partnerLogoOrPlaceholderUrl
	};
};
